import { useState, useCallback, useEffect } from 'react'
import axios from 'axios'
import { getProjectContracts } from '../services/projectService'
import { getAllChangeOrders } from '../services/changeOrderService'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3003'

/**
 * Custom hook for managing both contract and subcontract workflows
 * Combines contracts and subcontracts into a single unified list
 * @param {string} projectId - Project ID
 * @param {Object} user - Current user object
 * @param {string} token - Auth token
 * @param {Object} permissions - User permissions object (includes roleIds for role-based assignments)
 * @returns {Object} Hook state and methods
 */
export const useCostWorkflows = (projectId, user, token, permissions = null) => {
  const [loading, setLoading] = useState(false)
  const [workflows, setWorkflows] = useState([]) // Combined contracts + subcontracts
  const [stats, setStats] = useState({ total: 0, pendingApproval: 0, completed: 0, open: 0 })
  const [attachments, setAttachments] = useState({})
  const [packages, setPackages] = useState({})
  const [workflowHistory, setWorkflowHistory] = useState({})
  const [pendingApprovals, setPendingApprovals] = useState({})
  const [abortController, setAbortController] = useState(null)

  /**
   * Filter workflows by status
   */
  const filterByStatus = useCallback((items, statusKeywords) => {
    return items.filter(item => {
      const status = (item.status || '').toLowerCase()
      return statusKeywords.some(keyword => status.includes(keyword))
    })
  }, [])

  /**
   * Check if user matches a task assignee (including role-based assignments)
   */
  const isUserAssignedToTask = useCallback((task, user) => {
    if (!user) return false

    const currentUserEmail = user?.email?.toLowerCase()
    const currentUserId = user?.oxygenId || user?.id || user?.autodeskId || user?.userId
    const currentUsername = user?.name?.toLowerCase() || user?.username?.toLowerCase()
    // Get roleIds from permissions if available, otherwise from user object
    const currentUserRoleIds = permissions?.roleIds || user?.roleIds || []

    // Check if task is assigned by role (from backend pending-approvals endpoint)
    if (task._assignmentType === 'role') {
      // Match user's role IDs against the task's required role ID
      if (task.roleId && currentUserRoleIds.length > 0) {
        const roleMatch = currentUserRoleIds.includes(task.roleId)
        if (roleMatch) {
          console.log('[useCostWorkflows] Role-based assignment match:', {
            taskName: task.taskName,
            requiredRoleId: task.roleId,
            userRoleIds: currentUserRoleIds
          })
          return true
        }
      }
      return false
    }

    // Check if task is assigned to user directly
    if (task._assignmentType === 'user') {
      const assigneeId = task.oxygenId
      const assigneeEmail = task.email?.toLowerCase()
      const assigneeName = task.name?.toLowerCase()

      const idMatch = assigneeId && currentUserId && (
        assigneeId === currentUserId ||
        assigneeId.toString() === currentUserId.toString()
      )
      const emailMatch = assigneeEmail && currentUserEmail && assigneeEmail === currentUserEmail
      const nameMatch = assigneeName && currentUsername && assigneeName === currentUsername

      return idMatch || emailMatch || nameMatch
    }

    // Legacy handling for change orders (with assignees.users structure)
    if (task.assignees?.users && Array.isArray(task.assignees.users)) {
      const isAssigned = task.assignees.users.some(assignee => {
        const assigneeId = assignee.oxygenId || assignee.id || assignee.autodeskId
        const assigneeName = assignee.name?.toLowerCase()
        const assigneeEmail = assignee.email?.toLowerCase()

        const idMatch = assigneeId && currentUserId && (
          assigneeId === currentUserId ||
          assigneeId.toString() === currentUserId.toString()
        )
        const nameMatch = assigneeName && currentUsername && assigneeName === currentUsername
        const emailMatch = assigneeEmail && currentUserEmail && assigneeEmail === currentUserEmail

        return idMatch || nameMatch || emailMatch
      })

      return isAssigned
    }

    // Legacy handling for contracts (old assignee structure)
    const assigneeEmail = task.assignee?.email?.toLowerCase() || task.email?.toLowerCase()
    const assigneeId = task.assignee?.oxygenId || task.oxygenId || task.assignee?.id || task.assignee?.autodeskId || task.assigneeId || task.userId || task.id
    const assigneeUsername = task.assignee?.name?.toLowerCase() || task.assignee?.username?.toLowerCase() || task.name?.toLowerCase() || task.username?.toLowerCase()

    const emailMatch = assigneeEmail && currentUserEmail && assigneeEmail === currentUserEmail
    const idMatch = assigneeId && currentUserId && (
      assigneeId === currentUserId ||
      assigneeId.toString() === currentUserId.toString()
    )
    const nameMatch = assigneeUsername && currentUsername && assigneeUsername === currentUsername

    return emailMatch || idMatch || nameMatch
  }, [permissions])

  /**
   * Fetch pending approvals for contracts/subcontracts/change orders
   * Uses batching to limit concurrent requests and improve performance
   */
  const fetchPendingApprovals = useCallback(async (items) => {
    const pendingApprovalsData = {}

    // Process in batches to avoid overwhelming the server
    const BATCH_SIZE = 10
    const batches = []

    for (let i = 0; i < items.length; i += BATCH_SIZE) {
      batches.push(items.slice(i, i + BATCH_SIZE))
    }

    // Process batches sequentially, but items within each batch in parallel
    for (const batch of batches) {
      await Promise.all(batch.map(async (item) => {
        try {
          // For change orders, extract pending tasks from workflow data
          if (item._isChangeOrder) {
            // SCO forms use costStatus, OCO forms use budgetStatus
            const activeTasks = item.workflow?.costStatus?.currentStep?.activeTasks ||
                                item.workflow?.budgetStatus?.currentStep?.activeTasks ||
                                []
            pendingApprovalsData[item.id] = activeTasks
            return
          }

          // For contracts/subcontracts, use API endpoint
          const endpoint = item._isContract
            ? `${API_BASE_URL}/api/cost/${projectId}/contracts/${item.id}/pending-approvals`
            : `${API_BASE_URL}/api/cost/${projectId}/contracts/${item.id}/pending-approvals` // subcontracts use same endpoint

          const pendingRes = await axios.get(
            endpoint,
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              withCredentials: true
            }
          ).catch(() => ({ data: { success: false, pendingTasks: [] } }))

          pendingApprovalsData[item.id] = pendingRes.data.success ? (pendingRes.data.pendingTasks || []) : []
        } catch (_error) {
          pendingApprovalsData[item.id] = []
        }
      }))
    }

    return pendingApprovalsData
  }, [projectId, token])

  /**
   * Filter workflows by current user
   *
   * IMPORTANT: For individual project views, ALWAYS filter by user assignment
   * The shouldSkipReviewerFilter logic is for "All Projects" aggregated view only
   */
  const filterByCurrentUser = useCallback((items, pendingApprovalsData) => {
    // Always filter by user in individual project views
    if (!user) {
      return []  // No user, return empty array
    }

    return items.filter(item => {
      const pendingTasks = pendingApprovalsData[item.id] || []

      // If no pending tasks, skip this workflow
      if (pendingTasks.length === 0) {
        return false
      }

      // Check if user is assigned to any pending task
      const hasUserTask = pendingTasks.some(task => isUserAssignedToTask(task, user))

      return hasUserTask
    })
  }, [projectId, user, isUserAssignedToTask])

  /**
   * Fetch attachments for contracts/subcontracts/change orders
   */
  const fetchAttachments = useCallback(async (items) => {
    const attachmentsData = {}

    await Promise.all(items.map(async (item) => {
      try {
        // Change orders use different endpoint
        const endpoint = item._isChangeOrder
          ? `${API_BASE_URL}/api/cost/${projectId}/change-orders/${item.id}/attachments`
          : `${API_BASE_URL}/api/cost/${projectId}/contracts/${item.id}/attachments`

        const attachmentRes = await axios.get(
          endpoint,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            withCredentials: true
          }
        ).catch(() => ({ data: { success: false, attachments: [] } }))

        attachmentsData[item.id] = attachmentRes.data.success
          ? (attachmentRes.data.attachments || [])
          : []
      } catch (_error) {
        attachmentsData[item.id] = []
      }
    }))

    return attachmentsData
  }, [projectId, token])

  /**
   * Fetch packages for contracts/subcontracts/change orders
   */
  const fetchPackages = useCallback(async (items) => {
    const packagesData = {}

    await Promise.all(items.map(async (item) => {
      try {
        // Change orders use different endpoint
        const endpoint = item._isChangeOrder
          ? `${API_BASE_URL}/api/cost/${projectId}/change-orders/${item.id}/packages`
          : `${API_BASE_URL}/api/cost/${projectId}/contracts/${item.id}/packages`

        const packagesRes = await axios.get(
          endpoint,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            withCredentials: true
          }
        ).catch(() => ({ data: { success: false, packages: [] } }))

        packagesData[item.id] = packagesRes.data.success
          ? (packagesRes.data.packages || [])
          : []
      } catch (_error) {
        packagesData[item.id] = []
      }
    }))

    return packagesData
  }, [projectId, token])

  /**
   * Fetch workflow history for contracts/subcontracts
   */
  const fetchWorkflowHistory = useCallback(async (items) => {
    const historyData = {}

    await Promise.all(items.map(async (item) => {
      try {
        const endpoint = item._isContract
          ? `${API_BASE_URL}/api/cost/${projectId}/contracts/${item.id}/workflow-history`
          : `${API_BASE_URL}/api/cost/${projectId}/contracts/${item.id}/workflow-history`

        const historyRes = await axios.get(
          endpoint,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            withCredentials: true
          }
        ).catch(() => ({ data: { success: false, history: [] } }))

        historyData[item.id] = historyRes.data.success
          ? (historyRes.data.history || [])
          : []
      } catch (_error) {
        historyData[item.id] = []
      }
    }))

    return historyData
  }, [projectId, token])

  /**
   * Main fetch function - gets both contracts and subcontracts
   */
  const fetchWorkflows = useCallback(async () => {
    if (!projectId || !token) {
      return
    }

    setLoading(true)
    const perfStart = window.performance.now()

    try {
      // Fetch contracts and all change orders (SCO + OCO) in parallel
      console.log('[useCostWorkflows] Fetching contracts and change orders...')
      const fetchDataStart = window.performance.now()

      const [contractsData, changeOrdersData] = await Promise.all([
        getProjectContracts(projectId).catch(err => {
          console.error('[useCostWorkflows] Error fetching contracts:', err)
          return []
        }),
        getAllChangeOrders(projectId).catch(err => {
          console.error('[useCostWorkflows] Error fetching change orders (SCO + OCO):', err)
          return []
        })
      ])

      console.log(`[useCostWorkflows] Fetched ${contractsData?.length || 0} contracts and ${changeOrdersData?.length || 0} change orders in ${(window.performance.now() - fetchDataStart).toFixed(0)}ms`)

      // Mark all contracts with appropriate flags based on their type
      const contractWorkflows = (contractsData || []).map(c => {
        const contractType = c.type || 'Contract'
        const isSubcontract = contractType.toLowerCase() === 'subcontract'

        return {
          ...c,
          _isContract: !isSubcontract,
          _isSubcontract: isSubcontract,
          _workflowType: contractType
        }
      })

      // Mark all change orders with appropriate flags
      // They already have _changeOrderType from getAllChangeOrders()
      const changeOrderWorkflows = (changeOrdersData || []).map(co => ({
        ...co,
        _isChangeOrder: true,
        _workflowType: co._changeOrderType || 'Change Order' // Will be 'SCO' or 'OCO'
      }))

      // Combine contracts and change orders into a single array
      const allWorkflows = [...contractWorkflows, ...changeOrderWorkflows]

      // Filter by status - include all review-related statuses
      // This ensures forms awaiting review are shown regardless of exact status wording
      // Matches patterns used in ProjectContext.jsx and unifiedApprovalService.js
      const statusKeywords = ['review', 'submitted', 'pending', 'approval']
      const filteredByStatus = filterByStatus(allWorkflows, statusKeywords)

      // Early return if no workflows after status filter
      if (filteredByStatus.length === 0) {
        setWorkflows([])
        setStats({ total: 0, pendingApproval: 0, completed: 0, open: 0 })
        setPendingApprovals({})
        setAttachments({})
        setPackages({})
        setWorkflowHistory({})
        setLoading(false)
        return
      }

      // Fetch pending approvals for filtered workflows
      console.log(`[useCostWorkflows] Fetching pending approvals for ${filteredByStatus.length} workflows...`)
      const pendingApprovalsStart = window.performance.now()
      const pendingApprovalsData = await fetchPendingApprovals(filteredByStatus)
      console.log(`[useCostWorkflows] Fetched pending approvals in ${(window.performance.now() - pendingApprovalsStart).toFixed(0)}ms`)
      setPendingApprovals(pendingApprovalsData)

      // Filter by current user (only show workflows with tasks assigned to them)
      const filteredByUser = filterByCurrentUser(filteredByStatus, pendingApprovalsData)
      console.log(`[useCostWorkflows] ${filteredByUser.length} workflows assigned to current user`)

      // Set workflows immediately to show results faster (progressive loading)
      setWorkflows(filteredByUser)
      console.log(`[useCostWorkflows] Main data loaded in ${(window.performance.now() - perfStart).toFixed(0)}ms, fetching metadata in background...`)

      // Fetch additional data in background (non-blocking)
      // This allows the UI to render workflows immediately while metadata loads
      const metadataStart = window.performance.now()
      Promise.all([
        fetchAttachments(filteredByUser),
        fetchPackages(filteredByUser),
        fetchWorkflowHistory(filteredByUser)
      ]).then(([attachmentsData, packagesData, historyData]) => {
        setAttachments(attachmentsData)
        setPackages(packagesData)
        setWorkflowHistory(historyData)
        console.log(`[useCostWorkflows] Metadata loaded in ${(window.performance.now() - metadataStart).toFixed(0)}ms`)
        console.log(`[useCostWorkflows] Total time: ${(window.performance.now() - perfStart).toFixed(0)}ms`)
      }).catch(error => {
        console.error('[useCostWorkflows] Error fetching metadata:', error)
        // Set empty data on error
        setAttachments({})
        setPackages({})
        setWorkflowHistory({})
      })

      // Calculate stats
      const totalCount = filteredByUser.length
      const pendingCount = filteredByUser.filter(item => {
        const status = (item.status || '').toLowerCase()
        return status.includes('review')
      }).length
      const completedCount = filteredByUser.filter(item => {
        const status = (item.status || '').toLowerCase()
        return status.includes('completed') || status.includes('approved') || status.includes('closed')
      }).length
      const openCount = filteredByUser.filter(item => {
        const status = (item.status || '').toLowerCase()
        return status.includes('open') || status.includes('in progress')
      }).length

      setStats({
        total: totalCount,
        pendingApproval: pendingCount,
        completed: completedCount,
        open: openCount
      })

    } catch (error) {
      console.error('[useCostWorkflows] Error fetching workflows:', error)
      setWorkflows([])
      setStats({ total: 0, pendingApproval: 0, completed: 0, open: 0 })
    } finally {
      setLoading(false)
    }
  }, [
    projectId,
    token,
    filterByStatus,
    filterByCurrentUser,
    fetchPendingApprovals,
    fetchAttachments,
    fetchPackages,
    fetchWorkflowHistory
  ])

  // Clear state when projectId changes to prevent stale data
  useEffect(() => {
    // Clear all state when projectId changes
    setWorkflows([])
    setStats({ total: 0, pendingApproval: 0, completed: 0, open: 0 })
    setAttachments({})
    setPackages({})
    setWorkflowHistory({})
    setPendingApprovals({})

    // Abort any ongoing requests
    if (abortController) {
      abortController.abort()
    }

    // Create new abort controller for the new project
    const newController = new AbortController()
    setAbortController(newController)

    // Cleanup function
    return () => {
      newController.abort()
    }
  }, [projectId])

  return {
    loading,
    workflows, // Combined contracts + subcontracts
    stats,
    attachments,
    packages,
    workflowHistory,
    pendingApprovals,
    fetchWorkflows
  }
}
